import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { config } from "../config";
import { parse, stringify } from "qs";

const API_URL = `${config.API_URL}/43101_reports/`;

const callBackend = (url, params) => {
  return axios.get(url, {
    params: params,
    withCredentials: true,
    paramsSerializer: {
      encode: parse,
      serialize: (p) => stringify(p, { arrayFormat: "repeat" }),
    },
  });
};

const prepareSearchParams = (state) => {
  const params = {
    query: state.searchTerm,
    page: state.page,
    page_size: state.pageSize,
    date_from: state.dateFrom || "1970-01-01",
  };

  if (state.dateTo) {
    params.date_to = state.dateTo;
  }
  if (state.commodities.length > 0) {
    params.commodities = state.commodities;
  }
  if (state.depositStyles.length > 0) {
    params.deposit_styles = state.depositStyles;
  }
  if (state.projectNames.length > 0) {
    params.project_names = state.projectNames;
  }
  if (state.companyNames.length > 0) {
    params.company_names = state.companyNames;
  }
  if (state.reportTypes.length > 0) {
    params.report_types = state.reportTypes;
  }
  if (state.reportAuthors.length > 0) {
    params.report_authors = state.reportAuthors;
  }
  if (state.polygonString) {
    params.polygon_string = state.polygonString;
  }
  if (state.onlyHumanReviewed) {
    params.only_human_reviewed = state.onlyHumanReviewed;
  }

  return params;
};

export const searchReports43101 = createAsyncThunk(
  "reports43101/search",
  async (_, { getState }) => {
    try {
      const state = getState().reports43101Slice;
      const params = prepareSearchParams({ ...state, page: 1 });
      const resp = await callBackend(API_URL, params);
      return resp.data;
    } catch (e) {
      console.error(e);
      return {
        next_page: null,
        num_docs: 0,
        data: [],
        message: "Error while fetching the search results",
      };
    }
  }
);

export const fetchNextReports43101 = createAsyncThunk(
  "reports43101/fetchNext",
  async (_, { getState }) => {
    try {
      const state = getState().reports43101Slice;
      const params = prepareSearchParams({
        ...state,
        page: state.searchResults.next_page,
      });
      const resp = await callBackend(API_URL, params);
      return resp.data;
    } catch (e) {
      console.error(e);
      return {
        next_page: null,
        num_docs: 0,
        data: [],
        message: "Error while fetching the search results",
      };
    }
  }
);

export const reports43101Slice = createSlice({
  name: "reports43101",
  initialState: {
    searchResults: {},
    loadingResults: false,
    searchInitiated: false,
    searchTerm: "",
    page: 1,
    pageSize: 10,
    dateFrom: "",
    dateTo: "",
    commodities: [],
    depositStyles: [],
    projectNames: [],
    companyNames: [],
    reportTypes: [],
    reportAuthors: [],
    polygonString: "",
    onlyHumanReviewed: false,
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setDateFrom: (state, action) => {
      state.dateFrom = action.payload;
    },
    setDateTo: (state, action) => {
      state.dateTo = action.payload;
    },
    setCommodities: (state, action) => {
      state.commodities = action.payload;
    },
    setDepositStyles: (state, action) => {
      state.depositStyles = action.payload;
    },
    setProjectNames: (state, action) => {
      state.projectNames = action.payload;
    },
    setCompanyNames: (state, action) => {
      state.companyNames = action.payload;
    },
    setReportTypes: (state, action) => {
      state.reportTypes = action.payload;
    },
    setReportAuthors: (state, action) => {
      state.reportAuthors = action.payload;
    },
    setPolygonString: (state, action) => {
      state.polygonString = action.payload;
    },
    setOnlyHumanReviewed: (state, action) => {
      state.onlyHumanReviewed = action.payload;
    },
    clearFilters: (state) => {
      state.commodities = [];
      state.depositStyles = [];
      state.projectNames = [];
      state.companyNames = [];
      state.reportTypes = [];
      state.reportAuthors = [];
      state.dateFrom = "";
      state.dateTo = "";
      state.polygonString = "";
      state.onlyHumanReviewed = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchReports43101.fulfilled, (state, action) => {
      state.searchResults = action.payload;
      state.loadingResults = false;
    });
    builder.addCase(searchReports43101.pending, (state) => {
      state.searchResults = {};
      state.loadingResults = true;
      state.searchInitiated = true;
    });
    builder.addCase(fetchNextReports43101.fulfilled, (state, action) => {
      state.searchResults.data = [
        ...(state.searchResults.data || []),
        ...action.payload.data,
      ];
      state.searchResults.next_page = action.payload.next_page;
      state.loadingResults = false;
    });
    builder.addCase(fetchNextReports43101.pending, (state) => {
      state.loadingResults = true;
    });
  },
});

export const {
  setSearchTerm,
  setDateFrom,
  setDateTo,
  setCommodities,
  setDepositStyles,
  setProjectNames,
  setCompanyNames,
  setReportTypes,
  setReportAuthors,
  setPolygonString,
  setOnlyHumanReviewed,
  clearFilters,
} = reports43101Slice.actions;

export const reports43101Reducer = reports43101Slice.reducer;
